import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import ProgressBar from 'shared/components/feedback/ProgressBar/ProgressBar';
import {
  ServiceTypeLabels,
  ConsultRequestStatusEnum,
  ConsultRequestPagingStatusEnum,
} from 'utils/enums';

import { getConsultLabel } from 'utils/helpers';
import { formatIsoDate, formatToUserTimezone } from 'utils/dates';
import { Box, FlexBoxSpaceBetweenCenter } from 'styles/layout';
import {
  Container,
  ConsultInfoContainer,
  Column,
  BoldColumn,
} from './ConsultRequest.style';
import ConsultRequestDelete from './ConsultRequestDelete';
import ProviderPhoto from './ConsultRequestProviderPhoto';

const getConsultRequestPagingStatus = ({
  state: consultRequestStatus,
  pagingState,
  acceptedAt,
}) => {
  switch (consultRequestStatus) {
    case ConsultRequestStatusEnum.OPEN:
      return ConsultRequestPagingStatusEnum[pagingState];
    default:
      return (
        <>
          <Box>Accepted at</Box>
          <Box>{formatToUserTimezone(acceptedAt)}</Box>
        </>
      );
  }
};

const getConsultRequestProgress = (consultRequestStatus) => {
  switch (consultRequestStatus) {
    case ConsultRequestStatusEnum.ACCEPTED:
      return 50;
    case ConsultRequestStatusEnum.CLOSED:
      return 100;
    default:
      return 0;
  }
};

const ConsultRequest = ({
  id,
  firstName,
  lastName,
  age,
  dateOfBirth,
  facilityName,
  providerId,
  providerFirstName,
  providerLastName,
  createdAt,
  consultType,
  state,
  serviceType,
  pagingState,
  itemIndex,
  closedAt,
  acceptedAt,
  createdByFirstName,
  createdByLastName,
}) => {
  const providerName =
    providerFirstName && providerLastName
      ? `${providerFirstName} ${providerLastName}`
      : '';

  const renderProviderPhoto = useMemo(
    () => <ProviderPhoto providerId={providerId} />,
    [providerId],
  );

  return (
    <Container p={2} _index={itemIndex}>
      <ConsultInfoContainer>
        <FlexBoxSpaceBetweenCenter width="100%">
          <BoldColumn>
            <Box>{`${firstName} ${lastName}`}</Box>
            <Box>{age ? `${age} years old` : ''} {dateOfBirth ? `(${formatIsoDate(dateOfBirth)})` : ''}</Box>
          </BoldColumn>
          <BoldColumn>
            <Box>{facilityName}</Box>
            <Box>{ServiceTypeLabels[serviceType]}</Box>
            <Box>{getConsultLabel(consultType, serviceType)}</Box>
          </BoldColumn>
          <Column>
            <Box>Created By:</Box>
            <Box>{`${createdByFirstName} ${createdByLastName}`}</Box>
          </Column>
          <Column>
            <Box>Provider:</Box>
            <Box>{providerName}</Box>
          </Column>
          <Column>
            <Box>Created at</Box>
            <Box>{formatToUserTimezone(createdAt)}</Box>
          </Column>
          <Column>
            {getConsultRequestPagingStatus({
              state,
              pagingState,
              acceptedAt,
            })}
          </Column>
          {closedAt && (
            <Column>
              <Box>Consult Closed at</Box>
              <Box>{formatToUserTimezone(closedAt)}</Box>
            </Column>
          )}
          <Column width="25px">
            {state === ConsultRequestStatusEnum.OPEN && (
              <ConsultRequestDelete requestId={id} />
            )}
          </Column>
        </FlexBoxSpaceBetweenCenter>
        <Box mt={0.5}>
          <ProgressBar progress={getConsultRequestProgress(state)} />
        </Box>
      </ConsultInfoContainer>
      {renderProviderPhoto}
    </Container>
  );
};

ConsultRequest.propTypes = {
  id: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  age: PropTypes.number,
  dateOfBirth: PropTypes.string,
  facilityName: PropTypes.string.isRequired,
  providerId: PropTypes.number,
  providerFirstName: PropTypes.string,
  providerLastName: PropTypes.string,
  createdAt: PropTypes.string.isRequired,
  acceptedAt: PropTypes.string,
  consultType: PropTypes.string,
  closedAt: PropTypes.string,
  state: PropTypes.string.isRequired,
  pagingState: PropTypes.string,
  itemIndex: PropTypes.number.isRequired,
  createdByFirstName: PropTypes.string,
  createdByLastName: PropTypes.string,
  serviceType: PropTypes.string,
};

export default ConsultRequest;
